import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Autocomplete } from '@mantine/core';
import type { ComboboxStringItem } from '@mantine/core';

import { noop } from 'ui-components/default-props/noop';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { FlexColumn } from 'ui-components/components/flex';

import { checkProductDisabled } from './product-disabled-helper';
import { ProductTypeAheadInputResult } from './product-type-ahead-input-result';
import { useProductTypeAheadSearch } from './use-product-type-ahead-search';
import type { ProductSearchResult } from './use-product-type-ahead-search';
import { messages } from './messages';
import style from './style.module.css';

type ParentProduct = {
  name?: string;
  sku?: string;
};

type ProductTypeAheadInputProps = {
  ariaLabelledBy?: string;
  className?: string;
  disabled?: boolean;
  errorMessage?: React.ReactNode;
  excludeBundles?: boolean;
  name?: string;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (value: string) => void;
  onResultSelected?: (product: ProductSearchResult) => void;
  parentProduct?: ParentProduct;
  placeholder?: string;
  shouldShowAboveInput?: boolean;
  value?: string;
};

const MAX_DROPDOWN_HEIGHT = 280;
const DROPDOWN_CLASS_NAMES = { dropdown: style.dropdown, option: style.option };

const DISABLED_REASON_SELF = 'DISABLED_REASON_SELF';
const DISABLED_REASON_BUNDLE = 'DISABLED_REASON_BUNDLE';

export const ProductTypeAheadInput = ({
  ariaLabelledBy,
  className,
  disabled = false,
  errorMessage,
  excludeBundles,
  name,
  onBlur = noop,
  onChange = noop,
  onResultSelected = noop,
  parentProduct,
  placeholder,
  shouldShowAboveInput = false,
  value = '',
}: ProductTypeAheadInputProps) => {
  const intl = useIntl();
  const { results, clearResults, onSearchChange } = useProductTypeAheadSearch();

  const getDisabledReasonText = useCallback(
    (reasonKey?: typeof DISABLED_REASON_SELF | typeof DISABLED_REASON_BUNDLE): string | undefined => {
      if (reasonKey === DISABLED_REASON_SELF) {
        return intl.formatMessage(messages.cannotAddProductToItself);
      }
      if (reasonKey === DISABLED_REASON_BUNDLE) {
        return intl.formatMessage(messages.cannotAddBundleProductToBundle);
      }
      return undefined;
    },
    [intl],
  );

  const data = useMemo(
    () =>
      results.map((r) => ({
        value: r.productId,
        label: r.name,
        disabled: checkProductDisabled(r, excludeBundles, parentProduct).isDisabled,
      })),
    [results, excludeBundles, parentProduct],
  );

  // This configuration is necessary to allow manual configuration of the dropdown position via shouldShowAboveInput,
  // instead of the default behavior of rendering the dropdown above or below the input based on available space.
  const comboboxProps = useMemo(
    () => ({
      position: (shouldShowAboveInput ? 'top' : 'bottom') as 'top' | 'bottom',
      middlewares: { flip: false },
      preventPositionChangeWhenVisible: false,
      dropdownPadding: 0,
    }),
    [shouldShowAboveInput],
  );

  const handleChange = useCallback(
    (newValue: string) => {
      onSearchChange(newValue);
      onChange(newValue);
    },
    [onSearchChange, onChange],
  );

  const handleOptionSubmit = useCallback(
    (productId: string) => {
      const product = results.find((r) => r.productId === productId);
      if (product && !checkProductDisabled(product, excludeBundles, parentProduct).isDisabled) {
        clearResults();
        onResultSelected(product);
      }
    },
    [results, clearResults, onResultSelected, excludeBundles, parentProduct],
  );

  const renderOption = useCallback(
    ({ option }: { option: ComboboxStringItem }) => {
      const product = results.find((r) => r.productId === option.value);
      if (!product) {
        return null;
      }
      const { isDisabled, disabledReason } = checkProductDisabled(product, excludeBundles, parentProduct);
      const disabledReasonText = getDisabledReasonText(disabledReason);

      return (
        <ProductTypeAheadInputResult
          disabledReason={disabledReasonText}
          isDisabled={isDisabled}
          name={product.name}
          sku={product.sku}
          thumbnailUrl={product.thumbnailUrl}
        />
      );
    },
    [results, excludeBundles, parentProduct, getDisabledReasonText],
  );

  return (
    <FlexColumn>
      <Autocomplete
        aria-labelledby={ariaLabelledBy}
        className={className}
        classNames={DROPDOWN_CLASS_NAMES}
        comboboxProps={comboboxProps}
        data={data}
        disabled={disabled}
        error={!!errorMessage}
        filter={({ options }) => options}
        maxDropdownHeight={MAX_DROPDOWN_HEIGHT}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        onOptionSubmit={handleOptionSubmit}
        placeholder={placeholder}
        renderOption={renderOption}
        value={value}
      />
      {errorMessage ? <InlineMessage type={INLINE_MESSAGE_TYPES.error}>{errorMessage}</InlineMessage> : null}
    </FlexColumn>
  );
};
